import { formatDate } from '../utils/dateConverter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import AcquisitionDetail from '../components/AcquisitionDetail';
import CallList from '../components/CallList';
import KanbanBoard from '../components/KanbanBoard';
import SalesFunnel from '../components/SalesFunnel';
import AcquisitionModal from '../modals/AcquisitionModal';
import SalesToolbar from '../components/SalesToolbar';
import { mapAcquisitionDto } from '../dto/AcquisitionDTO';
import {
    dealTypeLabel,
    dealValue,
    followUpState,
    formatEur,
    formatEurShort,
    isFollowUpDue,
    isOpen,
    leadSourceLabel,
    pipelineKpis,
    probabilityOf,
    stageClass,
    stageLabel,
} from '../utils/salesPipeline';
import '../styles/ProjectAcquisition.css';
import { useToast, useConfirm } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';

const EMPTY_FILTERS = {
    search: '', stage: '', priority: '', type: '', source: '', owner: '', onlyDue: false,
};

/** One stat in the slim KPI bar; `onClick` turns it into a shortcut filter. */
function Stat({ label, value, hint, tone = '', onClick }) {
    const className = `acq-stat${tone ? ` acq-stat--${tone}` : ''}${onClick ? ' acq-stat--action' : ''}`;
    const body = (
        <>
            <span className="acq-stat-label">{label}</span>
            <span className="acq-stat-value">{value}</span>
        </>
    );
    return onClick
        ? <button type="button" className={className} title={hint} onClick={onClick}>{body}</button>
        : <span className={className} title={hint}>{body}</span>;
}

export default function ProjectAcquisition() {
    const toast = useToast();
    const confirm = useConfirm();
    const [acquisitions, setAcquisitions] = useState([]);
    const [viewMode, setViewMode] = useState('kanban');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showModal, setShowModal] = useState(false);
    const [editAcquisition, setEditAcquisition] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [loadError, setLoadError] = useState('');
    // Bumped whenever a deal changes here, so the call list refetches itself.
    const [callsToken, setCallsToken] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const loadAcquisitions = useCallback(() => (
        api.get('/ProjectAcquisition')
            .then((res) => { setAcquisitions(res.data.map(mapAcquisitionDto)); setLoadError(''); })
            .catch(() => setLoadError('Daten konnten nicht geladen werden. Bitte versuche es erneut.'))
    ), []);

    useEffect(() => { loadAcquisitions(); }, [loadAcquisitions]);

    // Deep link from the lead list / task bell: /project_acquisition?deal=<id>
    useEffect(() => {
        const dealId = searchParams.get('deal');
        if (!dealId) return;
        setDetailId(dealId);
        const next = new URLSearchParams(searchParams);
        next.delete('deal');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // The drawer always renders the freshest copy from the list.
    const detailAcquisition = useMemo(
        () => acquisitions.find(a => a.id === detailId) || null,
        [acquisitions, detailId]
    );

    const handlePatch = useCallback((id, payload) => (
        api.patch(`/ProjectAcquisition/${id}`, payload)
            .then(loadAcquisitions)
            // A changed stage or follow-up date can add or remove a call for today.
            .then(() => setCallsToken(t => t + 1))
    ), [loadAcquisitions]);

    const handleStatusChange = (id, newStatus) => {
        // Optimistic move so the card sticks where it was dropped.
        setAcquisitions((prev) => prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a)));
        handlePatch(id, { status: newStatus }).catch(err => {
            toast(extractApiError(err, 'Status konnte nicht gespeichert werden.'));
            loadAcquisitions();
        });
    };

    const handleDelete = (id) => {
        api.delete(`/ProjectAcquisition/${id}`)
            .then(() => { setDetailId(null); loadAcquisitions(); })
            .catch(err => toast(extractApiError(err, 'Löschen fehlgeschlagen.')));
    };

    const handleModalSave = () => {
        setShowModal(false);
        setEditAcquisition(null);
        loadAcquisitions();
    };

    const handleEdit = (acq) => {
        setDetailId(null);
        setEditAcquisition(acq);
        setShowModal(true);
    };

    const owners = useMemo(() => {
        const seen = new Map();
        acquisitions.forEach(a => { if (a.ownerUserId) seen.set(a.ownerUserId, a.ownerName || '—'); });
        return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], 'de'));
    }, [acquisitions]);

    const filtered = useMemo(() => acquisitions.filter((a) => {
        const term = filters.search.trim().toLowerCase();
        const matchSearch = !term
            || (a.projectTitle || '').toLowerCase().includes(term)
            || (a.companyName || '').toLowerCase().includes(term)
            || (a.contactPerson || '').toLowerCase().includes(term);
        return matchSearch
            && (!filters.stage || a.status === filters.stage)
            && (!filters.priority || a.priority === filters.priority)
            && (!filters.type || (a.dealType || 'project') === filters.type)
            && (!filters.source || a.leadSource === filters.source)
            && (!filters.owner || a.ownerUserId === filters.owner)
            && (!filters.onlyDue || isFollowUpDue(a));
    }), [acquisitions, filters]);

    const kpis = useMemo(() => pipelineKpis(acquisitions), [acquisitions]);

    const setFilter = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);
    const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

    return (
        <div className="acquisition-page">
            {loadError && (
                <div className="alert alert--error" style={{ marginBottom: 16 }}>
                    {loadError}
                    <button className="alert__close" onClick={loadAcquisitions} style={{ marginLeft: 12 }}>Erneut versuchen</button>
                </div>
            )}

            <div className="acq-header">
                <div className="acq-header-left">
                    <h2>Vertrieb</h2>
                </div>
                <div className="acq-header-right">
                    <button className="button" onClick={() => { setEditAcquisition(null); setShowModal(true); }}>
                        + Neuer Deal
                    </button>
                </div>
            </div>

            {/* KPI bar — details live in the tooltips to keep it one line tall */}
            <div className="acq-statbar">
                <Stat
                    label="Offen"
                    value={kpis.openCount}
                    hint={`${kpis.openCount} offene Deals · ${kpis.coldCount} aus Kaltakquise`}
                />
                <Stat
                    label="Pipeline"
                    value={formatEurShort(kpis.openValue)}
                    hint={`Wert der offenen Deals · Ø ${formatEurShort(kpis.avgDealSize)} pro Deal`}
                />
                <Stat
                    label="Gewichtet"
                    value={formatEurShort(kpis.weightedValue)}
                    hint="Pipeline-Wert nach Gewinnwahrscheinlichkeit"
                />
                <Stat
                    label="Quote"
                    value={kpis.winRate === null ? '—' : `${kpis.winRate} %`}
                    hint={`${kpis.wonCount} gewonnen · ${kpis.lostCount} verloren`}
                />
                <Stat
                    label="Gewonnen"
                    value={formatEurShort(kpis.wonValue)}
                    hint={`${kpis.wonCount} Abschlüsse`}
                    tone="success"
                />
                <Stat
                    label={kpis.overdueCount > 0 ? '⚠ Wiedervorlagen' : 'Wiedervorlagen'}
                    value={kpis.overdueCount + kpis.dueTodayCount}
                    hint={`Deals: ${kpis.overdueCount} überfällig · ${kpis.dueTodayCount} heute · ${kpis.missingFollowUpCount} ohne Termin — klicken zum Filtern (Leads stehen in der Anrufliste)`}
                    tone={kpis.overdueCount > 0 ? 'warn' : ''}
                    onClick={() => setFilter('onlyDue', !filters.onlyDue)}
                />
            </div>

            <SalesFunnel
                acquisitions={acquisitions}
                activeStage={filters.stage}
                onStageClick={(stage) => setFilter('stage', stage)}
            />

            <CallList
                onOpenDeal={setDetailId}
                onDataChange={loadAcquisitions}
                reloadToken={callsToken}
            />

            <SalesToolbar
                filters={filters}
                onChange={setFilter}
                onReset={resetFilters}
                owners={owners}
                viewMode={viewMode}
                onViewMode={setViewMode}
            />

            {viewMode === 'kanban' && (
                <KanbanBoard
                    acquisitions={filtered}
                    onStatusChange={handleStatusChange}
                    onCardClick={(acq) => setDetailId(acq.id)}
                />
            )}

            {viewMode === 'table' && (
                <div className="acq-table-wrap">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Deal</th>
                                <th>Firma</th>
                                <th>Typ</th>
                                <th>Phase</th>
                                <th>Quelle</th>
                                <th>Priorität</th>
                                <th className="acq-td-right">Wert</th>
                                <th className="acq-td-right">Chance</th>
                                <th>Wiedervorlage</th>
                                <th>Verantwortlich</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((acq) => (
                                <tr
                                    key={acq.id}
                                    className="acq-table-row"
                                    onClick={() => setDetailId(acq.id)}
                                >
                                    <td className="acq-td-title">
                                        {acq.projectTitle}
                                        {(acq.lineItems || []).length > 0 && (
                                            <span className="acq-pos-count">
                                                {acq.lineItems.length} Position{acq.lineItems.length === 1 ? '' : 'en'}
                                            </span>
                                        )}
                                    </td>
                                    <td>{acq.companyName}</td>
                                    <td>
                                        <span className={`acq-type type-${acq.dealType || 'project'}`}>
                                            {dealTypeLabel(acq.dealType)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`stage-badge ${stageClass(acq.status)}`}>
                                            {stageLabel(acq.status)}
                                        </span>
                                    </td>
                                    <td className="acq-td-muted">{leadSourceLabel(acq.leadSource)}</td>
                                    <td>
                                        <span className={`prio-badge prio-${(acq.priority || 'medium').toLowerCase()}`}>
                                            {acq.priority}
                                        </span>
                                    </td>
                                    <td className="acq-td-right acq-td-value">{formatEur(dealValue(acq))}</td>
                                    <td className="acq-td-right">{probabilityOf(acq)} %</td>
                                    <td>
                                        {acq.nextFollowUpDate ? (
                                            <span className={`acq-followup is-${followUpState(acq)}`}>
                                                {formatDate(acq.nextFollowUpDate)}
                                            </span>
                                        ) : (
                                            <span className={isOpen(acq) ? 'acq-followup is-missing' : 'acq-td-muted'}>
                                                {isOpen(acq) ? 'offen' : '—'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="acq-td-muted">{acq.ownerName || '—'}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button className="button-warning" onClick={() => handleEdit(acq)}>
                                            Bearbeiten
                                        </button>
                                        <button
                                            className="button-danger"
                                            onClick={async () => {
                                                if (await confirm(`"${acq.projectTitle}" wirklich löschen?`, { confirmLabel: 'Löschen', danger: true })) {
                                                    handleDelete(acq.id);
                                                }
                                            }}
                                        >
                                            Löschen
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: '32px', color: 'var(--color-text-muted)' }}>
                                        {acquisitions.length === 0
                                            ? 'Noch keine Deals. Lege den ersten Deal an, um die Pipeline zu füllen.'
                                            : 'Keine Deals für diese Filter.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {detailAcquisition && (
                <AcquisitionDetail
                    acquisition={detailAcquisition}
                    onClose={() => setDetailId(null)}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onPatch={handlePatch}
                    onFileChange={loadAcquisitions}
                />
            )}

            {showModal && (
                <AcquisitionModal
                    acquisition={editAcquisition}
                    onClose={() => { setShowModal(false); setEditAcquisition(null); }}
                    onSave={handleModalSave}
                />
            )}
        </div>
    );
}
